//-- Copyright (c) 2021 TMT Insights Group, LLC
import { createSelector } from '@reduxjs/toolkit';
import { isEqual } from 'lodash';
import { RootState } from '@/redux/store';
import { Search } from '@/redux/slices/slice_types';
import { selectUserPreferences } from '@/redux/selectors/authSelectors';
import { selectHasOrderCategories, selectTitleTypes } from '@/redux/selectors/constantsSelectors';
import { Filters, Settings, SortingObject } from '@/__configs/config_types';
import { LoadingStatuses } from '@/constants/constants';
import { COLUMNS } from '@/_utils/columns';

export const selectBulkActionConstants = (state: RootState) => state.constants.bulkActions;
export const selectDataTable = (state: RootState) => state.dataTable;
export const selectCurrentTableKey = createSelector([selectDataTable], (dataTable) => dataTable.currentTable || null);

export const selectCurrentTable = createSelector(
  [selectDataTable, selectCurrentTableKey],
  (dataTable, currentTableKey) => dataTable[currentTableKey as keyof typeof dataTable] || ({} as any)
);
export const selectCurrentModule = (state: RootState) => state.dataTable.currentModule || null;
export const selectCurrentTableLabel = createSelector(selectCurrentTable, (table) => table?.label || '');
export const selectCurrentTableData = createSelector(selectCurrentTable, (table) => table.data || []);
export const selectDataTableSettings = createSelector(selectCurrentTable, (table) => table.settings || ({} as Settings));
export const selectDataTableSorting = createSelector(selectDataTableSettings, (tableSettings) => tableSettings.sorting || {});
export const selectDataTableIsMultiSort = createSelector(selectCurrentTable, (table) => table.multiSort || false);
export const selectDataTableFilters = createSelector(selectDataTableSettings, (settings) => settings.filters || {});
export const selectDataTableParams = createSelector(selectCurrentTable, (settings) => settings.params || {});
export const selectDataTablePage = createSelector(selectDataTableSettings, (settings) => settings.currentPage || 1);
export const selectDataTableResults = createSelector(selectCurrentTable, (table) => table.pagination?.total || 0);
export const selectDataTableBaseUrl = createSelector(selectCurrentTable, (table) => table.baseUrl || '');
export const selectDataTableAppliedSearchName = createSelector(selectCurrentTable, (table) => table.appliedSearchName || null);
export const selectTableTitle = createSelector(selectCurrentTable, (table) => table.title || '');
export const selectRowEditedId = createSelector(selectCurrentTable, (table) => table.rowEditedId || '');
export const selectTableFilters = createSelector(selectCurrentTable, (table) => table.filters || {});
export const selectAssignedUserFilter = createSelector(selectDataTableFilters, (tableFilters) => tableFilters.assigned_users_filter || []);
export const selectUserFiltedByType = (type: any) => createSelector(selectDataTableFilters, (tableFilters) => tableFilters[type] || []);
export const selectAssignedGroupsFilter = createSelector(selectDataTableFilters, (tableFilters) => tableFilters.assigned_groups_filter || []);
export const selectExpandedRowIds = createSelector(selectCurrentTable, (table) => table.expandedRowIds || []);
export const selectDataTableDefaultSorting = createSelector(selectCurrentTable, (table) => table?.preset?.sorting || []);
export const selectDataTableType = createSelector(selectCurrentTable, (table) => table?.tableType || 'table');

export const selectDataTableMultiSortById = (column: string) =>
  createSelector(selectDataTableSettings, (settings) => {
    if (!settings.sorting) return { order: 'asc' };
    if ((settings.sorting as SortingObject)?.id)
      return (settings?.sorting as SortingObject)?.id === column
        ? { order: (settings?.sorting as SortingObject)?.order, id: column }
        : { order: 'asc' };
    const index = (settings.sorting as string[]).find((col) => col === column || col === `-${column}`);
    if (!index) return { order: 'asc' };
    return { order: index[0] === '-' ? 'desc' : 'asc', id: index.replace('-', ''), sortOrder: (settings.sorting as string[]).indexOf(index) };
  });

export const selectIsCurrentlyGridView = createSelector([selectUserPreferences, selectCurrentTableKey], (prefs, currentTableKey) => {
  return prefs?.[currentTableKey as keyof typeof prefs]?.grid || false;
});

// if disableGetData is not set (thus returning null/undefined), then getData should be enabled by default
export const selectShouldGetData = createSelector(selectTableFilters, (filters) =>
  (filters as Filters).disableGetData == null ? false : (filters as Filters).disableGetData
);
export const selectDataTableLoading = createSelector([selectCurrentTable], (table) => table.status === LoadingStatuses.pending || false);
export const selectDefaultTableColumns = createSelector([selectCurrentTableKey], (currentTableKey) =>
  COLUMNS[currentTableKey as keyof typeof COLUMNS]()
);

export const selectDataTableEnableGridViewToggle = createSelector([selectCurrentTable], (table) => table.enableGridViewToggle || false);

const hiddenOrderJobsCategory = (currentKey: string, columnKey: string, hasCategories: boolean) => {
  if (currentKey !== 'jobs' || columnKey !== 'category') return false;
  return !hasCategories;
};
export const selectUserColumns = createSelector(
  [selectCurrentTableKey, selectUserPreferences, selectHasOrderCategories],
  (currentKey, prefs, hasCategories) => {
    const userColumns = prefs?.[currentKey as keyof typeof prefs]?.columns;
    if (!currentKey) return [];
    // the default columns for the current table, will use the config overrides if they exist
    // and fall back to the default columns if they don't
    const allColumns = COLUMNS[currentKey as keyof typeof COLUMNS]?.();
    if (!allColumns) return [];

    // loop through all user saved columns and find the ones that exist in the default columns
    if (userColumns) {
      const cols = { ...allColumns };
      const savedColumns: any[] = [];
      userColumns.forEach((col: any) => {
        if (allColumns[col.id as keyof typeof allColumns] && !col.omitted) {
          savedColumns.push({
            field: col.id,
            ...(allColumns[col.id as keyof typeof allColumns] as any),
            ...col
          });
        }
        delete cols[col.id as keyof typeof cols];
      });
      // if we have any cols left over we can assume a new col has been made available, so add it
      // TODO: what if we want to remove a column a user has already saved to prefs?
      if (Object.keys(cols).length) {
        const allColumnKeys = Object.keys(allColumns);
        Object.keys(cols).map((key) => {
          const placementIndex = allColumnKeys.indexOf(key);
          const colToInsert = {
            ...(allColumns[key as keyof typeof allColumns] as any),
            order: placementIndex,
            id: key,
            field: key,
            hidden: (cols[key as keyof typeof cols] as any)?.hidden || hiddenOrderJobsCategory(currentKey as string, key, hasCategories)
          };
          if (!colToInsert.omitted) {
            savedColumns.splice(placementIndex, 0, colToInsert);
          }
        });
      }
      return savedColumns;
    } else {
      return Object.keys(allColumns)
        .filter((key) => !(allColumns[key as keyof typeof allColumns] as any)?.omitted)
        .map((key, i) => {
          const column = allColumns[key as keyof typeof allColumns];
          return {
            order: i,
            id: key,
            hidden: hiddenOrderJobsCategory(currentKey as string, key, hasCategories),
            field: key,
            ...(column as object)
          };
        });
    }
  }
);
export const selectExpandableColumn = createSelector([selectUserColumns], (userColumns) => {
  const expandableCol = userColumns.find((col) => col.field === 'expandable');
  return expandableCol;
});

export const selectIsExpandableTable = createSelector([selectUserColumns], (userColumns) => {
  const expandableCol = userColumns.find((col) => col.field === 'expandable');
  return !!expandableCol;
});

export const selectSavedSearchUpdated = (state: RootState) => {
  const appliedSearchName = selectDataTableAppliedSearchName(state);
  if (!appliedSearchName) return false;
  const tableKey = state.dataTable.currentTable;
  const filters = selectDataTableFilters(state);
  const sorting = selectDataTableSorting(state);
  const savedSearches = state.auth?.preferences?.[tableKey as keyof typeof state.auth.preferences]?.saved_searches;
  const savedSearch = savedSearches.find((ss: Search) => ss.name === appliedSearchName) || {};
  const { filters: savedFilters, sorting: savedSorting } = savedSearch;
  return !isEqual(filters, savedFilters) || !isEqual(sorting, savedSorting);
};

export const selectCurrentlySortingBy = createSelector(selectDataTableSorting, (sorting): string | string[] =>
  Array.isArray(sorting) ? sorting : (sorting as SortingObject)?.id
);
export const selectCurrentlySortingOrder = createSelector(selectDataTableSorting, (sorting) => (sorting as SortingObject)?.order);

export const selectCanExportReport = (state: RootState) => {
  const results = selectDataTableResults(state);
  if (!results) return false;
  const filters = selectDataTableFilters(state);
  const filterValues = Object.values(filters).map((x) => (typeof x === 'boolean' && !!x ? 'true' : x));
  return (
    filterValues.length !== 0 && filterValues.some((x) => x && (typeof x === 'string' || Array.isArray(x) || typeof x === 'object') && x.length !== 0)
  );
};

export const selectIsFiltered = (state: RootState) => {
  const filters = selectDataTableFilters(state);
  const filterValues = Object.values(filters).map((x) => (typeof x === 'boolean' && !!x ? 'true' : x));
  return (
    filterValues.length !== 0 && filterValues.some((x) => x && (typeof x === 'string' || Array.isArray(x) || typeof x === 'object') && x.length !== 0)
  );
};

// intersection between all possible filters and user-selected filters.
export const getFilteredFilters = (allFilters: Filters) => (state: RootState) => {
  const userFilters = selectTableFilters(state);
  if (!userFilters) return allFilters;
  const filteredFilters: any = {};
  Object.keys(userFilters).forEach((filterKey) => (filteredFilters[filterKey] = allFilters[filterKey]));
  return filteredFilters;
};

export const selectEntityTypesFilter = createSelector([selectDataTableParams, selectTitleTypes], (params, types) => {
  const { entity_types_filter } = params;
  const entityTypesFilter: any = {};
  let entityTypes = ['feature', 'series', 'episode'];
  if (entity_types_filter?.length) {
    entityTypes = entity_types_filter;
  }

  entityTypes.forEach((type: any) => {
    entityTypesFilter[type] = types[type as keyof typeof types];
  });

  return entityTypesFilter;
});

export const selectTaskFilterResolved = createSelector(selectDataTableParams, (params) => {
  if (!params.status_filter) return false;
  return params.status_filter.includes('resolved');
});

export const selectCheckedTableRows = createSelector(selectCurrentTable, (table) => table.selected || []);
export const selectCheckedTableRowsStatusStageCounts = createSelector(selectCheckedTableRows, (rows) => {
  const statusStageCounts = {} as any;
  rows.forEach((row: any) => {
    if (!statusStageCounts[row.status]) {
      statusStageCounts[row.status] = { stages: {}, count: 0 };
    }
    if (!row.stage) return statusStageCounts[row.status].count++;
    if (!statusStageCounts[row.status].stages[row.stage]) {
      statusStageCounts[row.status].stages[row.stage] = 1;
    } else {
      statusStageCounts[row.status].stages[row.stage]++;
    }
  });
  return statusStageCounts;
});

export const selectCurrentTableBulkActions = createSelector([selectCurrentTableKey, selectBulkActionConstants], (tableKey, actions) => {
  if (!actions || !tableKey) return null;
  if (tableKey === 'pendingAssets') return actions['jobs/assets' as keyof typeof actions] || null;
  return actions[tableKey] || null;
});

export const selectCurrentTableBulkActionsByStatusStage = createSelector([selectCurrentTableBulkActions], (actions) => {
  if (!actions) return {};
  const bulkActionsByStatusStage = {} as any;
  Object.keys(actions).forEach((action) => {
    actions[action].allowed_statuses.forEach((status: any) => {
      if (!bulkActionsByStatusStage[status.status]) {
        bulkActionsByStatusStage[status.status] = { stage: {}, actions: [] };
      }
      if (!status.stage) return bulkActionsByStatusStage[status.status].actions.push(action);

      if (!bulkActionsByStatusStage[status.status].stage[status.stage]) {
        bulkActionsByStatusStage[status.status].stage[status.stage] = [action];
      } else {
        bulkActionsByStatusStage[status.status].stage[status.stage].push(action);
      }
    });
  });
  return bulkActionsByStatusStage;
});

export const selectJobCancelBulkAction = createSelector(selectBulkActionConstants, (actions) =>
  actions?.jobs ? actions.jobs.cancel_job || actions.jobs.cancel || null : null
);

export const selectDataTableFilterOptions = (filter: string) =>
  createSelector([selectCurrentTable], (table) => (table?.filterOptions ? table?.filterOptions[filter] : undefined));

export const selectBulkErrors = createSelector(selectCurrentTable, (table: any): string[][] => table?.bulkErrors || []);
